import moment from "moment";

import { amountInOwnerCurrency } from "./financialStatus";

/**
 * Completed months for a year relative to `now`:
 * - current year → Jan through current month (inclusive)
 * - past years → Jan through Dec
 * - future years → empty
 */
export function completedMonthsForYear(year, now = moment()) {
	const current = moment(now);
	const selectedYear = Number(year);
	if (!Number.isFinite(selectedYear)) return [];
	if (selectedYear > current.year()) return [];

	const lastMonthIndex = selectedYear === current.year() ? current.month() : 11;
	const months = [];
	for (let monthIndex = 0; monthIndex <= lastMonthIndex; monthIndex += 1) {
		months.push({
			monthIndex,
			key: moment({ year: selectedYear, month: monthIndex }).format("YYYY-MM"),
			label: moment({ year: selectedYear, month: monthIndex }).format("MMMM"),
		});
	}
	return months;
}

function ownerTargetCurrency(property) {
	const raw = String(property?.owner?.bankDetails?.currency || "")
		.trim()
		.toLowerCase();
	return raw === "ron" ? "ron" : "eur";
}

function parsePaymentDate(value) {
	if (!value) return null;
	const parsed = moment(value, [moment.ISO_8601, "YYYY-MM-DD", "DD-MM-YYYY"], true);
	if (parsed.isValid()) return parsed;
	const fallback = moment(value);
	return fallback.isValid() ? fallback : null;
}

/**
 * Build RON/EUR administration-fee series for completed months of `year`.
 * Reuses FS currency conversion; does not apply expense signing (chart bars stay positive).
 */
export function buildAdministrationFeeChartSeries(properties = [], year, now = moment()) {
	const months = completedMonthsForYear(year, now);
	const ron = [];
	const eur = [];
	const categories = [];

	months.forEach(({ key, label }) => {
		let sRon = 0;
		let sEur = 0;

		(properties || []).forEach((property) => {
			const targetCurrency = ownerTargetCurrency(property);
			(property?.payments || []).forEach((payment) => {
				if (payment?.type !== "administrationFee") return;
				const paidAt = parsePaymentDate(payment.paymentDate);
				if (!paidAt || paidAt.format("YYYY-MM") !== key) return;

				// FS conversion compares currencies case-sensitively; normalize like the old Dashboard loop.
				const normalizedPayment = {
					...payment,
					currency: String(payment.currency || "")
						.trim()
						.toLowerCase(),
					exchange: payment.exchange
						? {
								...payment.exchange,
								currency: String(payment.exchange.currency || "")
									.trim()
									.toLowerCase(),
							}
						: payment.exchange,
				};
				const converted = amountInOwnerCurrency(normalizedPayment, targetCurrency);
				if (converted.skipped) return;

				const value = Math.abs(converted.amount);
				if (targetCurrency === "ron") sRon += value;
				else sEur += value;
			});
		});

		categories.push(label);
		ron.push(Number(sRon.toFixed(2)));
		eur.push(Number(sEur.toFixed(2)));
	});

	return { categories, ron, eur };
}

export function buildAdministrationFeeChartConfig(properties, year, now = moment()) {
	const { categories, ron, eur } = buildAdministrationFeeChartSeries(properties, year, now);
	return {
		options: {
			tooltip: {
				style: {
					backgroundColor: "#ccc !important",
					fontSize: "12px",
					fontFamily: undefined,
				},
			},
			xaxis: {
				categories,
				show: true,
				labels: {
					show: true,
					style: {
						fontSize: "12px",
					},
				},
				axisTicks: {
					show: false,
				},
			},
			yaxis: {
				show: true,
				color: "#000",
				labels: {
					show: true,
					style: {
						fontSize: "14px",
					},
				},
			},
			grid: {
				show: true,
			},
			dataLabels: {
				enabled: false,
			},
			plotOptions: {
				bar: {
					borderRadius: 4,
					borderRadiusApplication: "end",
					columnWidth: "18px",
				},
			},
			responsive: [
				{
					breakpoint: 768,
					options: {
						plotOptions: {
							bar: {
								borderRadius: 0,
							},
						},
					},
				},
			],
		},
		series: [
			{ data: ron, name: "ron" },
			{ data: eur, name: "eur" },
		],
	};
}
